import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, ChevronDown, ChevronUp, CalendarCheck, AlertTriangle, Phone, MapPin, Clock, Loader2, Search, Trash2, X, Plus } from 'lucide-react';
import { api } from '../../lib/api';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';

// Platform view of every patient referral (firm → clinic): which office it
// landed at, where it stands, whether the first visit is booked, and the
// signals that explain a stall — patient reachable? office hours set?
// who at the clinic was notified? The parties act in the app; the admin can
// resend a clinic alert or delete a referral (test rows, wrong patient).

const STATUS = {
  received:  ['bg-sky-500/10 text-sky-600', 'Awaiting acceptance'],
  accepted:  ['bg-amber-500/10 text-amber-600', 'Accepted — not booked'],
  scheduled: ['bg-emerald-500/10 text-emerald-600', 'First visit booked'],
  completed: ['bg-emerald-500/10 text-emerald-600', 'Care completed'],
  delivered: ['bg-emerald-500/10 text-emerald-600', 'Records delivered'],
  declined:  ['bg-red-500/10 text-red-500', 'Declined'],
  expired:   ['bg-red-500/10 text-red-500', 'Expired'],
};
const fmtPhone = (p) => { const d = String(p || '').replace(/\D/g, ''); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : d.length === 11 && d[0] === '1' ? `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}` : p; };
const titleCase = (s) => (s && s === s.toUpperCase() ? s.toLowerCase().replace(/\b([a-z])/g, (m) => m.toUpperCase()) : s); // ALL-CAPS org names read better
const ago = (iso) => { if (!iso) return '—'; const m = Math.round((Date.now() - new Date(iso)) / 60000); if (m < 60) return `${m}m ago`; const h = Math.round(m / 60); if (h < 48) return `${h}h ago`; return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' }); };
const fmt = (iso, opts) => (iso ? new Date(iso).toLocaleString([], opts || { dateStyle: 'medium', timeStyle: 'short' }) : '—');

// Why an accepted referral has no booking yet — the thing to act on.
function stallReason(r) {
  if (r.status !== 'accepted') return null;
  const reasons = [];
  if (!r.patient.email && !r.patient.has_login) reasons.push('Patient has no email, so no booking link went out — the firm or the clinic has to book for them');
  else if (!r.booking_invited_at) reasons.push('Booking link was never sent');
  if (r.office_hours_published === false) reasons.push('Office has no hours published (booking offers 8–6 default)');
  if (!reasons.length) reasons.push(`Patient was invited ${fmt(r.booking_invited_at)} and hasn't picked a time`);
  return reasons;
}

// Send a referral on a firm's behalf. Same thing the firm's own "Find a
// provider" does — same packet from the patient's file, same clinic
// text/call/email and acceptance window — so it shows up in both the
// firm's and the clinic's Referrals pages like any other.
function NewReferralModal({ onClose, onSent }) {
  const [parties, setParties] = useState(null);
  const [firmId, setFirmId] = useState('');
  const [clients, setClients] = useState(null);
  const [clientId, setClientId] = useState('');
  const [clinicId, setClinicId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [service, setService] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { api.adminReferralParties().then(setParties).catch((e) => setErr(e.message)); }, []);
  useEffect(() => {
    if (!firmId) { setClients(null); setClientId(''); return; }
    setClients(null); setClientId('');
    api.adminOrgClients(firmId).then(setClients).catch((e) => setErr(e.message));
  }, [firmId]);
  const clinic = parties?.clinics.find((c) => c.id === clinicId) || null;
  useEffect(() => { setLocationId(clinic?.locations?.length === 1 ? clinic.locations[0].id : ''); }, [clinicId]); // eslint-disable-line
  const send = async () => {
    setSending(true); setErr('');
    try {
      const r = await api.adminCreateCaseReferral({ firm_org_id: firmId, client_id: clientId, provider_org_id: clinicId, location_id: locationId || null, service });
      onSent(r);
    } catch (e) { setErr(e.message); }
    finally { setSending(false); }
  };
  const inputClass = 'w-full bg-tertiary border border-theme rounded-[9px] px-3 py-[8px] text-[12.5px] text-primary-c focus:outline-none focus:border-accent';
  const labelClass = 'block text-[10.5px] font-semibold uppercase tracking-wider text-muted-c mb-1.5';
  const ready = firmId && clientId && clinicId && !sending;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !sending && onClose()}>
      <div className="bg-elevated border border-theme rounded-[14px] p-5 max-w-[520px] w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-1.5">
          <div className="text-[15px] font-bold text-primary-c">Send a referral for a firm</div>
          <button onClick={onClose} disabled={sending} className="text-muted-c hover:text-primary-c"><X size={14} /></button>
        </div>
        <p className="text-[12.5px] text-muted-c mb-4">Goes out exactly as if the firm sent it: the clinic is texted, called and emailed with the patient's packet and has the usual window to accept. Both sides see it in Referrals.</p>
        <div className="space-y-3.5">
          <div>
            <label className={labelClass}>Sending firm</label>
            <select value={firmId} onChange={(e) => setFirmId(e.target.value)} className={inputClass} disabled={!parties}>
              <option value="">{parties ? 'Choose a firm…' : 'Loading…'}</option>
              {(parties?.firms || []).map((f) => <option key={f.id} value={f.id}>{f.name}{f.type === 'marketer' ? ' (marketer)' : ''}{f.state ? ` · ${f.state}` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Patient</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputClass} disabled={!firmId || !clients}>
              <option value="">{!firmId ? 'Pick the firm first' : !clients ? 'Loading…' : clients.length ? 'Choose a patient…' : 'This firm has no patients yet — add one in their Contacts'}</option>
              {(clients || []).map((c) => <option key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${fmtPhone(c.phone)}` : ''}{!c.phone && !c.email ? ' · no phone or email' : ''}</option>)}
            </select>
            {clientId && clients && !clients.find((c) => c.id === clientId)?.phone && (
              <div className="text-[11.5px] text-amber-600 mt-1.5 inline-flex items-center gap-1"><AlertTriangle size={11} /> No phone on file — the patient won't get the booking text; the firm or clinic will have to book for them.</div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Clinic</label>
              <select value={clinicId} onChange={(e) => setClinicId(e.target.value)} className={inputClass} disabled={!parties}>
                <option value="">Choose a clinic…</option>
                {(parties?.clinics || []).map((c) => <option key={c.id} value={c.id}>{titleCase(c.name)}{c.state ? ` · ${[c.city, c.state].filter(Boolean).join(', ')}` : ''}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Office</label>
              <select value={locationId} onChange={(e) => setLocationId(e.target.value)} className={inputClass} disabled={!clinic || clinic.locations.length <= 1}>
                <option value="">{!clinic ? '—' : clinic.locations.length === 0 ? 'No offices set up' : clinic.locations.length === 1 ? clinic.locations[0].name : 'Nearest to the patient'}</option>
                {clinic && clinic.locations.length > 1 && clinic.locations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.city ? ` · ${l.city}` : ''}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelClass}>Service (optional)</label>
            <input value={service} onChange={(e) => setService(e.target.value)} placeholder="e.g. Initial evaluation, MRI lumbar spine" className={inputClass} />
          </div>
        </div>
        {err && <div className="text-[12px] text-red-500 mt-3">{err}</div>}
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} disabled={sending} className="px-3 py-1.5 rounded-[8px] text-[12px] font-semibold text-muted-c hover:text-primary-c">Cancel</button>
          <button onClick={send} disabled={!ready} className="px-3.5 py-1.5 rounded-[8px] text-[12px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50 inline-flex items-center gap-1.5">{sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send referral</button>
        </div>
      </div>
    </div>
  );
}

export default function CaseReferrals() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all | open | booked | stalled
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState('');
  const [flash, setFlash] = useState({}); // referral id → message
  const [q, setQ] = useState('');
  const [firm, setFirm] = useState('');
  const [clinic, setClinic] = useState('');
  const [since, setSince] = useState(''); // '' | 7 | 30 | 90 (days)
  const [selected, setSelected] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null); // { ids, label }
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState('');
  const [creating, setCreating] = useState(false);

  const remove = async (ids) => {
    setDeleting(true);
    try {
      const out = ids.length === 1 ? await api.adminDeleteReferral(ids[0]) : await api.adminDeleteReferrals(ids);
      setRows((rs) => (rs || []).filter((r) => !ids.includes(r.id)));
      setSelected((sel) => { const n = new Set(sel); ids.forEach((id) => n.delete(id)); return n; });
      if (ids.includes(open)) setOpen(null);
      setNotice(`Deleted ${out.deleted} referral${out.deleted === 1 ? '' : 's'}${out.cancelled_appointments ? ` and cancelled ${out.cancelled_appointments} first visit${out.cancelled_appointments === 1 ? '' : 's'}` : ''}.`);
      setConfirm(null);
    } catch (e) { setNotice(e.message); }
    finally { setDeleting(false); }
  };
  const renotify = async (r) => {
    setBusy(r.id);
    try {
      const out = await api.adminRenotifyReferral(r.id);
      setFlash((f) => ({ ...f, [r.id]: `Sent to ${out.recipients?.length || 0} inbox${out.recipients?.length === 1 ? '' : 'es'} (${(out.recipients || []).join(', ')}), office called, window restarted.` }));
      setRows(await api.adminListCaseReferrals());
    } catch (e) { setFlash((f) => ({ ...f, [r.id]: e.message })); }
    finally { setBusy(''); }
  };

  useEffect(() => {
    api.adminListCaseReferrals().then(setRows).catch((e) => setError(e.message));
  }, []);

  const firms = useMemo(() => [...new Map((rows || []).map((r) => [r.firm?.id, r.firm?.name]).filter(([id]) => id)).entries()].sort((a, b) => a[1].localeCompare(b[1])), [rows]);
  const clinics = useMemo(() => [...new Map((rows || []).map((r) => [r.provider?.id, titleCase(r.provider?.name)]).filter(([id]) => id)).entries()].sort((a, b) => a[1].localeCompare(b[1])), [rows]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const digits = needle.replace(/\D/g, '');
    const cutoff = since ? Date.now() - Number(since) * 86400000 : 0;
    return (rows || []).filter((r) => {
      if (filter === 'booked' && !['scheduled', 'completed', 'delivered'].includes(r.status)) return false;
      if (filter === 'stalled' && !(r.status === 'accepted' || r.status === 'expired')) return false;
      if (filter === 'open' && !['received', 'accepted'].includes(r.status)) return false;
      if (firm && r.firm?.id !== firm) return false;
      if (clinic && r.provider?.id !== clinic) return false;
      if (cutoff && new Date(r.created_at).getTime() < cutoff) return false;
      if (needle) {
        const hay = [r.patient.name, r.patient.email, r.firm?.name, r.provider?.name, r.location?.name, r.location?.city, r.service, r.specialty].filter(Boolean).join(' ').toLowerCase();
        const phone = String(r.patient.phone || '').replace(/\D/g, '');
        if (!hay.includes(needle) && !(digits.length >= 3 && phone.includes(digits))) return false;
      }
      return true;
    });
  }, [rows, filter, q, firm, clinic, since]);
  const filtersOn = !!(q || firm || clinic || since);
  const shownSelected = shown.filter((r) => selected.has(r.id));
  const allShownSelected = shown.length > 0 && shownSelected.length === shown.length;
  const toggleAll = () => setSelected((sel) => { const n = new Set(sel); if (allShownSelected) shown.forEach((r) => n.delete(r.id)); else shown.forEach((r) => n.add(r.id)); return n; });
  const toggle = (id) => setSelected((sel) => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const counts = useMemo(() => {
    const c = { total: 0, booked: 0, stalled: 0, waiting: 0 };
    for (const r of rows || []) {
      c.total += 1;
      if (['scheduled', 'completed', 'delivered'].includes(r.status)) c.booked += 1;
      else if (r.status === 'accepted' || r.status === 'expired') c.stalled += 1;
      else if (r.status === 'received') c.waiting += 1;
    }
    return c;
  }, [rows]);

  if (error) return <div className="p-6 text-[13px] text-red-500">{error}</div>;
  if (rows === null) return <LoadingSpinner />;

  const thClass = 'text-left text-[10.5px] font-semibold text-muted-c uppercase tracking-wider px-4 py-2.5';
  const selectClass = 'bg-tertiary border border-theme rounded-[8px] px-2.5 py-[6px] text-[12px] text-primary-c focus:outline-none focus:border-accent max-w-[220px]';

  return (
    <div className="flex-1 overflow-y-auto bg-primary">
      <div className="max-w-[1440px] mx-auto px-4 md:px-7 py-6">
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div>
            <h1 className="text-[22px] font-bold text-primary-c flex items-center gap-2"><Send size={18} className="text-accent-light" /> Patient referrals</h1>
            <p className="text-[12.5px] text-muted-c mt-1 max-w-[640px]">Every referral a firm has sent a clinic, the office it went to, and whether the first visit is on the calendar. Accepted-but-unbooked rows say why.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-0.5 bg-tertiary p-1 rounded-[10px]">
              {[['all', `All ${counts.total}`], ['open', `Open ${counts.waiting + counts.stalled}`], ['stalled', `Not booked ${counts.stalled}`], ['booked', `Booked ${counts.booked}`]].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} className={`px-3 py-[6px] rounded-[7px] text-[12px] font-semibold transition-all ${filter === k ? 'bg-accent text-white shadow-glow' : 'text-muted-c hover:text-primary-c'}`}>{l}</button>
              ))}
            </div>
            <button onClick={() => setCreating(true)} className="px-3.5 h-[34px] rounded-[9px] text-[12px] font-semibold text-white gradient-accent shadow-glow inline-flex items-center gap-1.5"><Plus size={13} /> New referral</button>
          </div>
        </div>

        {creating && (
          <NewReferralModal
            onClose={() => setCreating(false)}
            onSent={async (r) => {
              setCreating(false);
              setNotice(`Referral sent for ${r.client_snapshot?.name || 'the patient'} — the clinic has been notified. It now shows in both Referrals pages.`);
              try { setRows(await api.adminListCaseReferrals()); } catch { /* list refresh is best effort */ }
            }}
          />
        )}

        <div className="flex items-center gap-2 flex-wrap mb-4">
          <label className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-c" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Patient, phone, firm, clinic, office" className="bg-tertiary border border-theme rounded-[8px] pl-8 pr-2.5 py-[6px] text-[12px] text-primary-c placeholder:text-muted-c focus:outline-none focus:border-accent w-[260px]" />
          </label>
          <select value={firm} onChange={(e) => setFirm(e.target.value)} className={selectClass}>
            <option value="">All firms</option>
            {firms.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select value={clinic} onChange={(e) => setClinic(e.target.value)} className={selectClass}>
            <option value="">All clinics</option>
            {clinics.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select value={since} onChange={(e) => setSince(e.target.value)} className={selectClass}>
            <option value="">Any time</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          {filtersOn && <button onClick={() => { setQ(''); setFirm(''); setClinic(''); setSince(''); }} className="text-[12px] text-muted-c hover:text-primary-c inline-flex items-center gap-1"><X size={12} /> Clear</button>}
          <span className="text-[12px] text-muted-c ml-auto">{shown.length} of {counts.total}</span>
        </div>

        {(selected.size > 0 || notice) && (
          <div className="flex items-center gap-3 flex-wrap mb-4 px-4 py-2.5 rounded-[10px] bg-elevated border border-theme text-[12.5px]">
            {selected.size > 0 && (
              <>
                <span className="text-primary-c font-semibold">{selected.size} selected</span>
                <button onClick={() => setConfirm({ ids: [...selected], label: `${selected.size} referral${selected.size === 1 ? '' : 's'}` })} className="px-3 py-1.5 rounded-[8px] text-[12px] font-semibold text-red-500 border border-red-500/40 hover:bg-red-500/10 inline-flex items-center gap-1.5"><Trash2 size={12} /> Delete selected</button>
                <button onClick={() => setSelected(new Set())} className="text-[12px] text-muted-c hover:text-primary-c">Clear selection</button>
              </>
            )}
            {notice && <span className="text-muted-c ml-auto inline-flex items-center gap-2">{notice} <button onClick={() => setNotice('')} className="text-muted-c hover:text-primary-c"><X size={12} /></button></span>}
          </div>
        )}

        {confirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !deleting && setConfirm(null)}>
            <div className="bg-elevated border border-theme rounded-[14px] p-5 max-w-[420px] w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="text-[15px] font-bold text-primary-c mb-1.5">Delete {confirm.label}?</div>
              <p className="text-[12.5px] text-muted-c">The referral disappears for the firm and the clinic. A first visit that was booked from it is cancelled unless it already happened. The patient stays on the clinic's roster. This can't be undone.</p>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setConfirm(null)} disabled={deleting} className="px-3 py-1.5 rounded-[8px] text-[12px] font-semibold text-muted-c hover:text-primary-c">Cancel</button>
                <button onClick={() => remove(confirm.ids)} disabled={deleting} className="px-3 py-1.5 rounded-[8px] text-[12px] font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 inline-flex items-center gap-1.5">{deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete</button>
              </div>
            </div>
          </div>
        )}

        {shown.length === 0 ? (
          <EmptyState title={filtersOn || filter !== 'all' ? 'No referrals match' : 'No referrals here'} description={filtersOn || filter !== 'all' ? 'Try clearing a filter.' : 'Referrals sent between firms and clinics will show up as they happen.'} />
        ) : (
          <div className="bg-elevated rounded-xl border border-theme overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-theme">
                    <th className="px-3 py-2.5 w-[36px]"><input type="checkbox" checked={allShownSelected} onChange={toggleAll} aria-label="Select all shown" className="accent-[var(--accent)] cursor-pointer" /></th>
                    <th className={thClass}>Patient</th>
                    <th className={thClass}>From → To</th>
                    <th className={thClass}>Office</th>
                    <th className={thClass}>Status</th>
                    <th className={thClass}>First visit</th>
                    <th className={thClass + ' hidden lg:table-cell'}>Sent</th>
                    <th className={thClass}></th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const [cls, label] = STATUS[r.status] || STATUS.received;
                    const isOpen = open === r.id;
                    const reasons = stallReason(r);
                    return (
                      <React.Fragment key={r.id}>
                        <tr onClick={() => setOpen(isOpen ? null : r.id)} className={`border-b border-theme last:border-0 hover:bg-tertiary/40 transition-colors cursor-pointer ${selected.has(r.id) ? 'bg-accent-glow' : ''}`}>
                          <td className="px-3 py-3 w-[36px]" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} aria-label="Select referral" className="accent-[var(--accent)] cursor-pointer" /></td>
                          <td className="px-4 py-3 max-w-[220px]">
                            <div className="text-[13px] font-semibold text-primary-c truncate" title={r.patient.name || ''}>{r.patient.name || 'Patient'}</div>
                            <div className="text-[11.5px] text-muted-c truncate flex items-center gap-1.5">
                              {r.patient.phone ? <span className="inline-flex items-center gap-1 whitespace-nowrap"><Phone size={9} /> {fmtPhone(r.patient.phone)}</span> : <span>no phone</span>}
                              {!r.patient.email && <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" title="No email on file — no booking link was sent" />}
                            </div>
                          </td>
                          <td className="px-4 py-3 max-w-[240px] text-[12.5px]">
                            <div className="text-primary-c truncate" title={r.firm?.name || ''}>{r.firm?.name}</div>
                            <div className="text-muted-c truncate" title={r.provider?.name || ''}>→ {titleCase(r.provider?.name)}</div>
                          </td>
                          <td className="px-4 py-3 max-w-[200px] text-[12.5px]">
                            {r.location ? (
                              <div className="min-w-0">
                                <div className="text-primary-c truncate inline-flex items-center gap-1.5 max-w-full" title={r.location.name}><MapPin size={12} className="text-accent-light shrink-0" /><span className="truncate">{r.location.name}</span></div>
                                <div className="text-[11px] text-muted-c truncate">{[r.location.city, r.location.state].filter(Boolean).join(', ')}{r.office_hours_published === false ? ' · no hours set' : ''}</div>
                              </div>
                            ) : <span className="text-muted-c">—</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>{label}</span>
                            {r.status === 'accepted' && r.hours_since_accept != null && <div className="text-[11px] text-muted-c mt-1">{r.hours_since_accept < 48 ? `${r.hours_since_accept}h` : `${Math.round(r.hours_since_accept / 24)}d`} waiting</div>}
                            {r.status === 'received' && <div className="text-[11px] text-muted-c mt-1">accept by {fmt(r.expires_at, { timeStyle: 'short' })}</div>}
                          </td>
                          <td className="px-4 py-3 text-[12.5px] max-w-[220px]">
                            {r.appointment ? (
                              <div className="min-w-0">
                                <div className="text-primary-c inline-flex items-center gap-1.5 whitespace-nowrap"><CalendarCheck size={13} className="text-emerald-500 shrink-0" /> {fmt(r.appointment.starts_at)}</div>
                                <div className="text-[11px] text-muted-c truncate" title={`${r.appointment.provider || 'any provider'} · ${r.appointment.status}${r.appointment.office ? ` · ${r.appointment.office}` : ''}`}>
                                  {r.appointment.provider || 'any provider'} · {r.appointment.status}{r.appointment.office && r.appointment.office !== r.location?.name ? <span className="text-amber-600"> · at {r.appointment.office}</span> : ''}
                                </div>
                              </div>
                            ) : r.scheduled_time ? fmt(r.scheduled_time) : <span className="inline-flex items-center gap-1 text-amber-600 whitespace-nowrap"><AlertTriangle size={12} /> not booked</span>}
                          </td>
                          <td className="px-4 py-3 text-[12px] text-muted-c hidden lg:table-cell whitespace-nowrap" title={fmt(r.created_at)}>{ago(r.created_at)}</td>
                          <td className="px-3 py-3.5 text-muted-c">{isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</td>
                        </tr>
                        {isOpen && (
                          <tr className="border-b border-theme last:border-0 bg-tertiary/30">
                            <td colSpan={8} className="px-5 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[12.5px]">
                                <div>
                                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-c mb-1.5">What's holding it</div>
                                  {reasons ? (
                                    <ul className="space-y-1">{reasons.map((x, i) => <li key={i} className="flex items-start gap-1.5 text-primary-c"><AlertTriangle size={12} className="text-amber-500 mt-0.5 shrink-0" /> {x}</li>)}</ul>
                                  ) : <div className="text-muted-c">Nothing — {label.toLowerCase()}.</div>}
                                  <div className="mt-3 text-[11.5px] text-muted-c">
                                    {r.service && <div>{r.service}</div>}
                                    {r.billing_chip && <div>Billing: {r.billing_chip}</div>}
                                    {r.minutes_to_accept != null && <div>Accepted in {r.minutes_to_accept} min</div>}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-c mb-1.5">Clinic contact</div>
                                  {['received', 'expired'].includes(r.status) && (
                                    <div className="mb-2">
                                      <button onClick={(e) => { e.stopPropagation(); renotify(r); }} disabled={busy === r.id} className="px-3 py-1.5 rounded-[8px] text-[12px] font-semibold text-white gradient-accent shadow-glow disabled:opacity-50 inline-flex items-center gap-1.5">
                                        {busy === r.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Resend notification
                                      </button>
                                      {flash[r.id] && <div className="text-[11.5px] text-success mt-1.5">{flash[r.id]}</div>}
                                    </div>
                                  )}
                                  <div className="text-primary-c">{r.provider?.name}</div>
                                  {r.location?.phone && <div className="text-muted-c inline-flex items-center gap-1"><Phone size={10} /> office {r.location.phone}</div>}
                                  {!r.location?.phone && r.provider?.phone && <div className="text-muted-c inline-flex items-center gap-1"><Phone size={10} /> {r.provider.phone}</div>}
                                  <div className="text-[11.5px] text-muted-c mt-1">Notified: {r.clinic_notify.length ? r.clinic_notify.join(', ') : 'nobody on file'}</div>
                                  {r.calls.length > 0 && <div className="text-[11.5px] text-muted-c mt-1">Calls: {r.calls.map((c) => c.note).join(' · ')}</div>}
                                  <div className="mt-2 flex gap-3 text-[11.5px]">
                                    <Link to={`/admin/organizations/${r.provider?.id}`} className="text-accent-light">Clinic</Link>
                                    <Link to={`/admin/organizations/${r.firm?.id}`} className="text-accent-light">Firm</Link>
                                    {r.patient.id && <Link to={`/admin/users/${r.patient.id}`} className="text-accent-light">Patient</Link>}
                                    <button onClick={(e) => { e.stopPropagation(); setConfirm({ ids: [r.id], label: `the referral for ${r.patient.name || 'this patient'}` }); }} className="text-red-500 hover:text-red-600 inline-flex items-center gap-1 ml-auto"><Trash2 size={11} /> Delete referral</button>
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-c mb-1.5">Timeline</div>
                                  <ul className="space-y-1">
                                    {r.timeline.map((t, i) => (
                                      <li key={i} className="flex items-start gap-1.5 text-primary-c"><Clock size={11} className="text-muted-c mt-0.5 shrink-0" /><span><span className="text-muted-c">{fmt(t.at, { dateStyle: 'short', timeStyle: 'short' })}</span> — {t.note}</span></li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
